import React, { useState, useEffect, useRef } from 'react';
import { Gift, Building2, Package, CheckCircle, Send, Phone, Mail, User, Briefcase, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CorporateGifting = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    quantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const sectionRef = useRef(null);

  // All corporate gifting images
  const sliderImages = [
    {
      url: "/images/gifting/gift1.jpg",
      alt: "Corporate Gifting with RSD Products - Diwali Gift Basket"
    },
    {
      url: "/images/gifting/gift2.jpg",
      alt: "Corporate Gifting - Budget Friendly, On-Time Delivery, Premium Products"
    },
    {
      url: "/images/gifting/gift3.jpg",
      alt: "Premium Gift Box with Chargers, Cables and Earbuds"
    },
    {
      url: "/images/gifting/gift4.jpg",
      alt: "Corporate Gifting - Excellence of a gift lies in its appropriateness"
    },
    {
      url: "/images/gifting/gift5.png",
      alt: "Premium Mobile Accessories Gift Sets"
    }
  ];

  // Auto-slide every 3 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  // Intersection Observer for visibility and confetti trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          setShowConfetti(true);
          // Hide confetti after 4 seconds
          setTimeout(() => setShowConfetti(false), 4000);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/corporate-inquiry`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', company: '', email: '', phone: '', quantity: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  const benefits = [
    'Custom branding & logo printing',
    'Bulk order discounts',
    'Premium gift packaging',
    'Dedicated account manager'
  ];

  // Generate confetti particles
  const confettiColors = ['#FF4D00', '#FFD700', '#FF6B35', '#FFA500', '#FF8C00', '#FF1493', '#00CED1', '#32CD32'];
  const confettiParticles = Array.from({ length: 80 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
    size: 8 + Math.random() * 12,
    rotation: Math.random() * 360,
    type: Math.random() > 0.5 ? 'circle' : 'rect'
  }));

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-24 bg-gradient-to-br from-neutral-50 via-orange-50/20 to-amber-50/30 relative overflow-hidden" 
      data-testid="corporate-gifting-section"
    >
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden">
          {confettiParticles.map((particle) => (
            <div
              key={particle.id}
              className="absolute animate-confetti-fall"
              style={{
                left: `${particle.left}%`,
                top: '-20px',
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
              }}
            >
              {particle.type === 'circle' ? (
                <div
                  className="rounded-full"
                  style={{
                    width: `${particle.size}px`,
                    height: `${particle.size}px`,
                    backgroundColor: particle.color,
                    transform: `rotate(${particle.rotation}deg)`,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: `${particle.size}px`,
                    height: `${particle.size * 0.6}px`,
                    backgroundColor: particle.color,
                    transform: `rotate(${particle.rotation}deg)`,
                  }}
                />
              )}
            </div>
          ))}
          
          {/* Party Pops / Bursts */}
          <div className="absolute top-10 left-10 animate-burst-1">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="4" fill="#FF4D00" className="animate-ping" />
              {[...Array(8)].map((_, i) => (
                <line
                  key={i}
                  x1="30"
                  y1="30"
                  x2={30 + 25 * Math.cos((i * 45 * Math.PI) / 180)}
                  y2={30 + 25 * Math.sin((i * 45 * Math.PI) / 180)}
                  stroke="#FFD700"
                  strokeWidth="2"
                  className="animate-pulse"
                />
              ))}
            </svg>
          </div>
          <div className="absolute top-20 right-20 animate-burst-2">
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="3" fill="#FF6B35" className="animate-ping" />
              {[...Array(6)].map((_, i) => (
                <line
                  key={i}
                  x1="25"
                  y1="25"
                  x2={25 + 20 * Math.cos((i * 60 * Math.PI) / 180)}
                  y2={25 + 20 * Math.sin((i * 60 * Math.PI) / 180)}
                  stroke="#FFA500"
                  strokeWidth="2"
                  className="animate-pulse"
                />
              ))}
            </svg>
          </div>
          <div className="absolute top-32 left-1/3 animate-burst-3">
            <svg width="40" height="40" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="3" fill="#FFD700" className="animate-ping" />
              {[...Array(8)].map((_, i) => (
                <circle
                  key={i}
                  cx={20 + 15 * Math.cos((i * 45 * Math.PI) / 180)}
                  cy={20 + 15 * Math.sin((i * 45 * Math.PI) / 180)}
                  r="3"
                  fill={confettiColors[i % confettiColors.length]}
                  className="animate-pulse"
                />
              ))}
            </svg>
          </div>
        </div>
      )}

      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header with Animation */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#FF4D00] to-[#FFD700] text-white px-5 py-2.5 rounded-full text-sm font-bold mb-4 shadow-lg animate-bounce-slow">
            <Gift className="w-5 h-5" />
            B2B Solutions
            <span className="ml-1">🎁</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Corporate <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-[#FFD700]">Gifting</span>
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            Impress your clients, employees, and partners with premium mobile accessories
          </p>
        </div>

        {/* Main Content - Left Text, Right Image Slider */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Side - Content */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <h3 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
              Premium Corporate Gift Solutions for Your Business
            </h3>
            <p className="text-neutral-600 mb-6 text-lg leading-relaxed">
              At RSD Accessories, we understand the importance of making a lasting impression. 
              Our corporate gifting solutions offer premium quality mobile accessories that reflect 
              your brand's commitment to excellence. From custom-branded chargers to elegant gift sets, 
              we help you create memorable experiences for your stakeholders.
            </p>
            <p className="text-neutral-600 mb-8 leading-relaxed">
              Whether it's for employee rewards, client appreciation, festival gifting, or promotional 
              events, our dedicated B2B team ensures seamless execution from concept to delivery. 
              We offer competitive bulk pricing, custom packaging options, and timely delivery 
              across India.
            </p>

            {/* Benefits List with Animation */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-3 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'}`}
                  style={{ transitionDelay: `${400 + index * 100}ms` }}
                >
                  <div className="w-7 h-7 bg-gradient-to-br from-[#FF4D00] to-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-neutral-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full shadow-md border border-orange-100 hover:shadow-lg transition-shadow">
                <Building2 className="w-5 h-5 text-[#FF4D00]" />
                <span className="text-sm font-semibold text-neutral-700">50+ Corporate Clients</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-full shadow-md border border-orange-100 hover:shadow-lg transition-shadow">
                <Package className="w-5 h-5 text-[#FF4D00]" />
                <span className="text-sm font-semibold text-neutral-700">MOQ: 50 Units</span>
              </div>
            </div>
          </div>

          {/* Right Side - Image Slider */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl bg-white">
              {/* Slider Container */}
              <div className="relative aspect-square overflow-hidden">
                {sliderImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                      index === currentSlide 
                        ? 'opacity-100 scale-100' 
                        : 'opacity-0 scale-105'
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-20"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-6 h-6 text-neutral-700" />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-20"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-6 h-6 text-neutral-700" />
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                  {sliderImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        index === currentSlide 
                          ? 'bg-[#FF4D00] w-8' 
                          : 'bg-white/70 hover:bg-white'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-[#FF4D00]/20 to-[#FFD700]/20 rounded-full -z-10 animate-pulse"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-orange-200 to-amber-200 rounded-full -z-10"></div>
          </div>
        </div>

        {/* Contact Form Section with Animation */}
        <div className={`bg-white rounded-3xl shadow-2xl overflow-hidden border border-orange-100 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="grid lg:grid-cols-5">
            {/* Form Info - Left Side */}
            <div className="lg:col-span-2 bg-gradient-to-br from-[#FF4D00] via-[#FF6B35] to-[#FFD700] p-8 md:p-10 text-white relative overflow-hidden">
              {/* Animated Background Shapes */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 animate-pulse"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 animate-pulse" style={{ animationDelay: '1s' }}></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Get a Custom Quote
                </h3>
                <p className="text-white/90 mb-8">
                  Fill out the form and our corporate sales team will get back to you within 24 hours 
                  with a customized quotation.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Email Us</p>
                      <a href="mailto:contact@rsdaccessories.com" className="text-white/90 hover:text-white transition-colors">
                        contact@rsdaccessories.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Call Us</p>
                      <a href="tel:+918595459161" className="text-white/90 hover:text-white transition-colors">
                        +91 85954 59161
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
                      <Gift className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Minimum Order</p>
                      <p className="text-white/90">50 units per product</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form - Right Side */}
            <div className="lg:col-span-3 p-8 md:p-10">
              <h4 className="text-xl font-bold text-neutral-800 mb-6 flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#FF4D00]" />
                Contact for Corporate Gifting
              </h4>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="corporate-gifting-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Name */}
                  <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '600ms' }}>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Your Name *
                    </label>
                    <div className="relative group">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#FF4D00] transition-colors" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full pl-11 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-[#FF4D00]/20 focus:border-[#FF4D00] outline-none transition-all hover:border-neutral-300"
                        data-testid="corporate-form-name"
                      />
                    </div>
                  </div>

                  {/* Company */}
                  <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '700ms' }}>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Company Name *
                    </label>
                    <div className="relative group">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#FF4D00] transition-colors" />
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        placeholder="ABC Corporation"
                        className="w-full pl-11 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-[#FF4D00]/20 focus:border-[#FF4D00] outline-none transition-all hover:border-neutral-300"
                        data-testid="corporate-form-company"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  {/* Email */}
                  <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '800ms' }}>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative group">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#FF4D00] transition-colors" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        className="w-full pl-11 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-[#FF4D00]/20 focus:border-[#FF4D00] outline-none transition-all hover:border-neutral-300"
                        data-testid="corporate-form-email"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '900ms' }}>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="relative group">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#FF4D00] transition-colors" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 98765 43210"
                        className="w-full pl-11 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-[#FF4D00]/20 focus:border-[#FF4D00] outline-none transition-all hover:border-neutral-300"
                        data-testid="corporate-form-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '1000ms' }}>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Estimated Quantity *
                  </label>
                  <div className="relative group">
                    <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 group-focus-within:text-[#FF4D00] transition-colors" />
                    <select
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                      className="w-full pl-11 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-[#FF4D00]/20 focus:border-[#FF4D00] outline-none transition-all appearance-none bg-white hover:border-neutral-300 cursor-pointer"
                      data-testid="corporate-form-quantity"
                    >
                      <option value="">Select quantity range</option>
                      <option value="50-100">50 - 100 units</option>
                      <option value="100-250">100 - 250 units</option>
                      <option value="250-500">250 - 500 units</option>
                      <option value="500-1000">500 - 1000 units</option>
                      <option value="1000+">1000+ units</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '1100ms' }}>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Your Requirements
                  </label>
                  <div className="relative group">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-neutral-400 group-focus-within:text-[#FF4D00] transition-colors" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us about your gifting requirements, preferred products, customization needs, etc."
                      className="w-full pl-11 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-[#FF4D00]/20 focus:border-[#FF4D00] outline-none transition-all resize-none hover:border-neutral-300"
                      data-testid="corporate-form-message"
                    ></textarea>
                  </div>
                </div>

                {/* Submit Button */}
                <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '1200ms' }}>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#FF4D00] to-[#FF6B35] hover:from-[#E04400] hover:to-[#FF4D00] text-white rounded-xl py-4 text-base font-semibold transition-all disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    data-testid="corporate-form-submit"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Submit Inquiry
                      </span>
                    )}
                  </Button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2 animate-fade-in">
                    <CheckCircle className="w-5 h-5" />
                    <span>Thank you! We'll get back to you within 24 hours.</span>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl animate-fade-in">
                    Something went wrong. Please try again or email us directly.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-confetti-fall {
          animation: confetti-fall linear forwards;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        
        .animate-burst-1 {
          animation: fade-in 0.5s ease-out forwards;
        }
        
        .animate-burst-2 {
          animation: fade-in 0.7s ease-out forwards;
        }
        
        .animate-burst-3 {
          animation: fade-in 0.9s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default CorporateGifting;
